import React, { useState, useRef, useCallback } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area
} from 'recharts';
import { Download, TrendingUp, BarChart3, PieChartIcon, Eye, EyeOff, DollarSign, Target, AlertTriangle } from 'lucide-react';
import {
  CHART_PALETTE,
  CHART_DONE_COLOR,
  CHART_NEGATIVE_COLOR,
  CHART_AXIS_STYLE,
  CHART_GRID_STYLE,
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_LEGEND_STYLE,
  getSeriesColor,
} from '../config/chartTheme';
import { formatCurrency } from '../utils/financialFormat';
import { FinancialValue } from './shared/FinancialValue';

interface PLVisualizationProps {
  financialData: Record<string, any[]>;
  reconstructedPL?: {
    by_year: Record<string, { revenue: number; cogs: number; payroll: number; expenses: number }>;
    totals: any;
  };
  industryBenchmarks?: {
    cogs_pct: number;
    payroll_pct: number;
    expenses_pct: number;
  };
  eventYear?: string;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Updated color palette using brand theme
const COLORS = {
  reconstructed: {
    primary: CHART_PALETTE[0],    // Brand orange
    secondary: CHART_PALETTE[1],  // Gray 400
    tertiary: CHART_PALETTE[2],   // Gray 500
  },
  benchmark: {
    primary: CHART_PALETTE[3],    // Gray 600
    secondary: CHART_PALETTE[4],  // Gray 300
  },
  event: {
    highlight: CHART_NEGATIVE_COLOR, // Muted gray for event highlight
  },
  chart: {
    revenue: CHART_PALETTE[0],     // Brand orange
    grossProfit: CHART_PALETTE[1], // Gray 400
    netProfit: CHART_PALETTE[2],   // Gray 500
    cogs: CHART_PALETTE[3],        // Gray 600
    payroll: CHART_PALETTE[4],     // Gray 300
    expenses: CHART_PALETTE[5],    // Gray 700
    benchmark: CHART_PALETTE[3],   // Gray 600
  }
};

// Pie chart colors using brand palette
const PIE_COLORS = [
  CHART_PALETTE[0], // Brand orange
  CHART_PALETTE[1], // Gray 400
  CHART_PALETTE[2], // Gray 500
  CHART_PALETTE[3], // Gray 600
  CHART_PALETTE[4], // Gray 300
  CHART_PALETTE[5], // Gray 700
];

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

export default function PLVisualization({
  financialData,
  reconstructedPL,
  industryBenchmarks,
  eventYear
}: PLVisualizationProps) {
  const [chartType, setChartType] = useState<'trends' | 'breakdown' | 'expenses' | 'comparison'>('trends');
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [showBenchmark, setShowBenchmark] = useState(true);
  const chartRef = useRef<HTMLDivElement>(null);

  // Default industry benchmarks if not provided
  const benchmarks = industryBenchmarks || {
    cogs_pct: 35,
    payroll_pct: 28,
    expenses_pct: 15
  };

  // Get available years from reconstructedPL
  const years = Object.keys(reconstructedPL?.by_year || {}).sort();

  // Set default selected year
  React.useEffect(() => {
    if (years.length > 0 && !selectedYear) {
      setSelectedYear(years[years.length - 1]); // Most recent year
    }
  }, [years, selectedYear]);

  // Prepare trend data for line chart (Revenue, Gross Profit, Net Profit over years)
  const trendData = React.useMemo(() => {
    if (!reconstructedPL?.by_year) return [];

    return Object.entries(reconstructedPL.by_year)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([year, data]) => {
        const revenue = data.revenue || 0;
        const cogs = data.cogs || 0;
        const payroll = data.payroll || 0;
        const expenses = data.expenses || 0;
        const grossProfit = revenue - cogs;
        const netProfit = revenue - cogs - payroll - expenses;

        // Calculate benchmark values based on revenue
        const benchmarkGrossProfit = revenue * (1 - benchmarks.cogs_pct / 100);
        const benchmarkNetProfit = revenue * (1 - benchmarks.cogs_pct / 100 - benchmarks.payroll_pct / 100 - benchmarks.expenses_pct / 100);

        return {
          year,
          revenue,
          grossProfit,
          netProfit,
          benchmarkGrossProfit: showBenchmark ? benchmarkGrossProfit : null,
          benchmarkNetProfit: showBenchmark ? benchmarkNetProfit : null,
          isEventYear: year === eventYear
        };
      });
  }, [reconstructedPL, benchmarks, showBenchmark, eventYear]);

  // Prepare bar chart data for expense breakdown
  const breakdownData = React.useMemo(() => {
    if (!selectedYear || !reconstructedPL?.by_year?.[selectedYear]) return [];

    const yearData = reconstructedPL.by_year[selectedYear];
    const revenue = yearData.revenue || 0;

    return [
      {
        category: 'COGS',
        reconstructed: yearData.cogs || 0,
        benchmark: revenue * (benchmarks.cogs_pct / 100),
        pctOfRevenue: revenue > 0 ? ((yearData.cogs || 0) / revenue * 100) : 0
      },
      {
        category: 'Payroll',
        reconstructed: yearData.payroll || 0,
        benchmark: revenue * (benchmarks.payroll_pct / 100),
        pctOfRevenue: revenue > 0 ? ((yearData.payroll || 0) / revenue * 100) : 0
      },
      {
        category: 'Other Expenses',
        reconstructed: yearData.expenses || 0,
        benchmark: revenue * (benchmarks.expenses_pct / 100),
        pctOfRevenue: revenue > 0 ? ((yearData.expenses || 0) / revenue * 100) : 0
      }
    ];
  }, [selectedYear, reconstructedPL, benchmarks]);

  // Prepare pie chart data for expense category split
  const expensePieData = React.useMemo(() => {
    if (!selectedYear || !reconstructedPL?.by_year?.[selectedYear]) return [];

    const yearData = reconstructedPL.by_year[selectedYear];
    const total = (yearData.cogs || 0) + (yearData.payroll || 0) + (yearData.expenses || 0);

    if (total === 0) return [];

    return [
      { name: 'COGS', value: yearData.cogs || 0, percent: ((yearData.cogs || 0) / total * 100) },
      { name: 'Payroll', value: yearData.payroll || 0, percent: ((yearData.payroll || 0) / total * 100) },
      { name: 'Other Expenses', value: yearData.expenses || 0, percent: ((yearData.expenses || 0) / total * 100) }
    ].filter(item => item.value > 0);
  }, [selectedYear, reconstructedPL]);

  // Prepare comparison data (Your % vs Industry %)
  const comparisonData = React.useMemo(() => {
    if (!reconstructedPL?.by_year) return [];

    return Object.entries(reconstructedPL.by_year)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([year, data]) => {
        const revenue = data.revenue || 0;
        if (revenue === 0) return null;

        return {
          year,
          'Your COGS %': Number(((data.cogs || 0) / revenue * 100).toFixed(1)),
          'Industry COGS %': benchmarks.cogs_pct,
          'Your Payroll %': Number(((data.payroll || 0) / revenue * 100).toFixed(1)),
          'Industry Payroll %': benchmarks.payroll_pct,
          'Your Expenses %': Number(((data.expenses || 0) / revenue * 100).toFixed(1)),
          'Industry Expenses %': benchmarks.expenses_pct,
          isEventYear: year === eventYear
        };
      })
      .filter(Boolean);
  }, [reconstructedPL, benchmarks, eventYear]);

  // Export chart as PNG
  const exportChart = useCallback(async () => {
    if (!chartRef.current) return;

    try {
      // Use html2canvas if available, otherwise show alert
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });

      const link = document.createElement('a');
      link.download = `pl-visualization-${chartType}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      // Fallback: copy SVG
      setExportMessage('Chart export requires html2canvas. Please take a screenshot manually.');
    }
  }, [chartType]);

  if (!reconstructedPL?.by_year || Object.keys(reconstructedPL.by_year).length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }}>
        <PieChartIcon className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-dim)' }} />
        <p style={{ color: 'var(--color-text-muted)' }}>No financial data available for visualization</p>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-dim)' }}>Complete the cleanup process to see charts</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl shadow-lg overflow-hidden border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[var(--color-bg-surface)] via-[var(--color-bg-elevated)] to-[var(--color-bg-surface)] border-b border-[var(--color-border)] px-6 py-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">P&L Visual Comparison</h3>
              <p className="text-xs text-[var(--color-text-muted)]">Reconstructed data vs industry benchmarks</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Benchmark Toggle */}
            <button
              onClick={() => setShowBenchmark(!showBenchmark)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showBenchmark
                  ? 'bg-[var(--color-brand)] text-white'
                  : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)]'
              }`}
            >
              {showBenchmark ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Benchmark
            </button>

            {/* Export Button */}
            <button
              onClick={exportChart}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)] transition-colors"
            >
              <Download className="w-4 h-4" />
              Export PNG
            </button>
          </div>
        </div>

        {/* Chart Type Selector */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button
            onClick={() => setChartType('trends')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              chartType === 'trends'
                ? 'bg-[var(--color-brand)] text-white shadow-md'
                : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Trends
          </button>
          <button
            onClick={() => setChartType('breakdown')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              chartType === 'breakdown'
                ? 'bg-[var(--color-brand)] text-white shadow-md'
                : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Breakdown
          </button>
          <button
            onClick={() => setChartType('expenses')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              chartType === 'expenses'
                ? 'bg-[var(--color-brand)] text-white shadow-md'
                : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
            }`}
          >
            <PieChartIcon className="w-4 h-4" />
            Expense Split
          </button>
          <button
            onClick={() => setChartType('comparison')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              chartType === 'comparison'
                ? 'bg-[var(--color-brand)] text-white shadow-md'
                : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            vs Industry %
          </button>
        </div>

        {/* Year Selector for breakdown and expenses views */}
        {(chartType === 'breakdown' || chartType === 'expenses') && years.length > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-[var(--color-text-secondary)] font-medium">Year:</span>
            {years.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  selectedYear === year
                    ? 'bg-[var(--color-brand)] text-white shadow-md'
                    : year === eventYear
                    ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)]'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                }`}
              >
                {year}
                {year === eventYear && <AlertTriangle size={12} className="ml-1 inline" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chart Area */}
      <div className="p-6" ref={chartRef}>
        {/* TRENDS - Line Chart */}
        {chartType === 'trends' && (
          <div>
            <h4 className="text-sm font-medium text-[var(--color-text-secondary)] mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[var(--color-brand)]" />
              Revenue, Gross Profit & Net Profit Trends
            </h4>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                <XAxis
                  dataKey="year"
                  tick={CHART_AXIS_STYLE}
                  tickLine={{ stroke: 'var(--color-border)' }}
                />
                <YAxis
                  tickFormatter={(v) => formatCurrency(v)}
                  tick={CHART_AXIS_STYLE}
                  tickLine={{ stroke: 'var(--color-border)' }}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [formatCurrency(value), name]}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                />
                <Legend wrapperStyle={CHART_LEGEND_STYLE} />

                {/* Main Lines */}
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke={COLORS.chart.revenue}
                  strokeWidth={3}
                  dot={{ fill: COLORS.chart.revenue, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="grossProfit"
                  name="Gross Profit"
                  stroke={COLORS.chart.grossProfit}
                  strokeWidth={3}
                  dot={{ fill: COLORS.chart.grossProfit, strokeWidth: 2, r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="netProfit"
                  name="Net Profit"
                  stroke={COLORS.chart.netProfit}
                  strokeWidth={3}
                  dot={{ fill: COLORS.chart.netProfit, strokeWidth: 2, r: 5 }}
                />

                {/* Benchmark Lines (dashed) */}
                {showBenchmark && (
                  <>
                    <Line
                      type="monotone"
                      dataKey="benchmarkGrossProfit"
                      name="Industry Gross Profit"
                      stroke={COLORS.benchmark.primary}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="benchmarkNetProfit"
                      name="Industry Net Profit"
                      stroke={COLORS.benchmark.secondary}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>

            {/* Growth Summary Cards */}
            {trendData.length >= 2 && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'revenue', label: 'Revenue', colorIndex: 0, Icon: DollarSign },
                  { key: 'grossProfit', label: 'Gross Profit', colorIndex: 1, Icon: TrendingUp },
                  { key: 'netProfit', label: 'Net Profit', colorIndex: 2, Icon: Target }
                ].map(({ key, label, colorIndex, Icon }) => {
                  const first = trendData[0][key as keyof typeof trendData[0]] as number;
                  const last = trendData[trendData.length - 1][key as keyof typeof trendData[0]] as number;
                  const growth = first > 0 ? ((last - first) / first * 100) : 0;

                  return (
                    <div key={key} className="p-4 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-[var(--color-text-muted)] inline-flex items-center gap-1"><Icon size={14} /> {label}</span>
                        <span className={`text-sm font-bold ${growth >= 0 ? 'text-[var(--color-verified)]' : 'text-[var(--color-text-muted)]'}`}>
                          {growth >= 0 ? '↑' : '↓'} {Math.abs(growth).toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-[var(--color-text-primary)]"><FinancialValue value={last} /></span>
                        <span className="text-xs text-[var(--color-text-muted)]">({trendData[trendData.length - 1].year})</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* BREAKDOWN - Bar Chart */}
        {chartType === 'breakdown' && (
          <div>
            <h4 className="text-sm font-medium text-[var(--color-text-secondary)] mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[var(--color-brand)]" />
              Expense Breakdown - {selectedYear}
              {selectedYear === eventYear && <span className="text-[var(--color-text-muted)] text-xs ml-2 inline-flex items-center gap-1"><AlertTriangle size={12} /> Event Year</span>}
            </h4>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={breakdownData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                <XAxis
                  dataKey="category"
                  tick={CHART_AXIS_STYLE}
                />
                <YAxis
                  tickFormatter={(v) => formatCurrency(v)}
                  tick={CHART_AXIS_STYLE}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [formatCurrency(value), name]}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                />
                <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                <Bar
                  dataKey="reconstructed"
                  name="Reconstructed"
                  fill={COLORS.reconstructed.primary}
                  radius={[4, 4, 0, 0]}
                />
                {showBenchmark && (
                  <Bar
                    dataKey="benchmark"
                    name="Industry Benchmark"
                    fill={COLORS.benchmark.primary}
                    radius={[4, 4, 0, 0]}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>

            {/* Variance Analysis */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              {breakdownData.map((item) => {
                const variance = item.reconstructed - item.benchmark;
                const variancePct = item.benchmark > 0 ? (variance / item.benchmark * 100) : 0;
                const isOver = variance > 0;

                return (
                  <div key={item.category} className="p-4 bg-[var(--color-bg-elevated)] rounded-lg border border-[var(--color-border)]">
                    <h5 className="font-medium text-[var(--color-text-primary)] mb-2">{item.category}</h5>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-muted)]">Actual:</span>
                        <span className="font-medium text-[var(--color-text-primary)]"><FinancialValue value={item.reconstructed} /></span>
                      </div>
                      {showBenchmark && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-[var(--color-text-muted)]">Benchmark:</span>
                            <span className="font-medium text-[var(--color-text-secondary)]"><FinancialValue value={item.benchmark} /></span>
                          </div>
                          <div className={`flex justify-between pt-1 border-t border-[var(--color-border)] ${isOver ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-verified)]'}`}>
                            <span>Variance:</span>
                            <span className="font-bold">
                              {formatCurrency(variance)} ({variancePct.toFixed(1)}%)
                            </span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-xs text-[var(--color-text-muted)] pt-1">
                        <span>% of Revenue:</span>
                        <span>{item.pctOfRevenue.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* EXPENSES - Pie Chart */}
        {chartType === 'expenses' && (
          <div>
            <h4 className="text-sm font-medium text-[var(--color-text-secondary)] mb-4 flex items-center gap-2">
              <PieChartIcon className="w-4 h-4 text-[var(--color-brand)]" />
              Expense Category Split - {selectedYear}
              {selectedYear === eventYear && <span className="text-[var(--color-text-muted)] text-xs ml-2 inline-flex items-center gap-1"><AlertTriangle size={12} /> Event Year</span>}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={expensePieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${percent.toFixed(1)}%`}
                    outerRadius={100}
                    fill={CHART_PALETTE[0]}
                    dataKey="value"
                  >
                    {expensePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={CHART_TOOLTIP_STYLE}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Expense Legend with values */}
              <div className="flex flex-col justify-center">
                <h5 className="font-medium text-[var(--color-text-primary)] mb-4">Expense Distribution</h5>
                <div className="space-y-3">
                  {expensePieData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center justify-between p-3 bg-[var(--color-bg-elevated)] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                        />
                        <span className="font-medium text-[var(--color-text-secondary)]">{entry.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[var(--color-text-primary)]"><FinancialValue value={entry.value} /></div>
                        <div className="text-xs text-[var(--color-text-muted)]">{entry.percent.toFixed(1)}% of total</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="mt-4 p-3 bg-[var(--color-needs-human-tint)] rounded-lg border border-[var(--color-needs-human-tint-bd)]">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-[var(--color-text-primary)]">Total Expenses</span>
                    <span className="text-xl font-bold text-[var(--color-brand)]">
                      <FinancialValue value={expensePieData.reduce((sum, item) => sum + item.value, 0)} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* COMPARISON - Your % vs Industry % */}
        {chartType === 'comparison' && (
          <div>
            <h4 className="text-sm font-medium text-[var(--color-text-secondary)] mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[var(--color-brand)]" />
              Your Ratios vs Industry Benchmarks (% of Revenue)
            </h4>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={comparisonData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
              >
                <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                <XAxis
                  type="number"
                  domain={[0, 50]}
                  tickFormatter={(v) => `${v}%`}
                  tick={CHART_AXIS_STYLE}
                />
                <YAxis
                  type="category"
                  dataKey="year"
                  tick={CHART_AXIS_STYLE}
                  width={60}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [`${value}%`, name]}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                />
                <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                <Bar dataKey="Your COGS %" fill={COLORS.chart.cogs} radius={[0, 4, 4, 0]} />
                {showBenchmark && <Bar dataKey="Industry COGS %" fill={CHART_PALETTE[4]} radius={[0, 4, 4, 0]} />}
                <Bar dataKey="Your Payroll %" fill={COLORS.chart.payroll} radius={[0, 4, 4, 0]} />
                {showBenchmark && <Bar dataKey="Industry Payroll %" fill={CHART_PALETTE[5]} radius={[0, 4, 4, 0]} />}
              </BarChart>
            </ResponsiveContainer>

            {/* Benchmark Comparison Table */}
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--color-bg-elevated)]">
                    <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">Year</th>
                    <th className="px-4 py-2 text-center font-medium text-[var(--color-text-secondary)]">COGS %</th>
                    <th className="px-4 py-2 text-center font-medium text-[var(--color-text-secondary)]">Payroll %</th>
                    <th className="px-4 py-2 text-center font-medium text-[var(--color-text-secondary)]">Expenses %</th>
                    <th className="px-4 py-2 text-center font-medium text-[var(--color-text-secondary)]">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row: any) => {
                    const margin = 100 - row['Your COGS %'] - row['Your Payroll %'] - row['Your Expenses %'];
                    return (
                      <tr
                        key={row.year}
                        className={`border-b border-[var(--color-border-faint)] ${row.isEventYear ? 'bg-[var(--color-needs-human-tint)]' : 'hover:bg-[var(--color-bg-hover)]'}`}
                      >
                        <td className="px-4 py-3 font-medium">
                          {row.year}
                          {row.isEventYear && <AlertTriangle size={14} className="ml-2 inline text-[var(--color-brand)]" />}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={row['Your COGS %'] > benchmarks.cogs_pct ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-verified)]'}>
                            {row['Your COGS %']}%
                          </span>
                          {showBenchmark && <span className="text-[var(--color-text-dim)] ml-1">/ {benchmarks.cogs_pct}%</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={row['Your Payroll %'] > benchmarks.payroll_pct ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-verified)]'}>
                            {row['Your Payroll %']}%
                          </span>
                          {showBenchmark && <span className="text-[var(--color-text-dim)] ml-1">/ {benchmarks.payroll_pct}%</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={row['Your Expenses %'] > benchmarks.expenses_pct ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-verified)]'}>
                            {row['Your Expenses %']}%
                          </span>
                          {showBenchmark && <span className="text-[var(--color-text-dim)] ml-1">/ {benchmarks.expenses_pct}%</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-bold ${margin > 20 ? 'text-[var(--color-verified)]' : margin > 10 ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-muted)]'}`}>
                            {margin.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Benchmark Note */}
            <div className="mt-4 p-3 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text-secondary)]">
              <strong>Industry Benchmarks:</strong> Based on BLS/NAICS data for your business type.
              <span className="text-[var(--color-verified)] font-medium"> Green</span> = better than industry,
              <span className="text-[var(--color-text-muted)] font-medium"> Gray</span> = higher than industry average.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
